import type { ExternalUserRowStatus } from '../../services/api/externalUsers';

/**
 * Status pill for external user rows.
 *
 * Single source of truth for the 4-color palette (pending / active / locked / deleted)
 * referenced in 15-UI-SPEC §"Status badge" and the threat model entry T-15-03-08.
 *
 * The component is purely presentational — no hooks, no state. Status colors
 * intentionally diverge from `AppAccessStatusChip` (yellow vs blue for "pending")
 * to avoid false visual similarity between the two surfaces (UI-SPEC §Color).
 */

const STATUS_STYLES = {
  pending: { label: 'Pending', classes: 'bg-yellow-100 text-yellow-800' },
  active: { label: 'Active', classes: 'bg-green-100 text-green-800' },
  locked: { label: 'Locked', classes: 'bg-orange-100 text-orange-800' },
  deleted: { label: 'Deleted', classes: 'bg-gray-100 text-gray-500' },
} as const;

export interface ExternalUserStatusBadgeProps {
  status: ExternalUserRowStatus;
}

export function ExternalUserStatusBadge({ status }: ExternalUserStatusBadgeProps) {
  const { label, classes } = STATUS_STYLES[status];
  return (
    <span
      role="status"
      aria-label={`Account status: ${label}`}
      className={`inline-flex items-center rounded-full font-semibold px-2 py-1 text-xs ${classes}`}
    >
      {label}
    </span>
  );
}
